import './i18n';
import React, { useEffect, useMemo, useState, lazy, Suspense } from "react";
import { Alert, Box, CssBaseline } from "@mui/material";
import Footer from "./components/Footer";
import Menu from "./components/Menu";
import ModernHeader from "./components/ModernHeader";
import { ThemeProvider } from "@mui/material/styles";
import { createAppTheme } from "./theme";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom"; // Asegúrate de importar useNavigate
import HomePage from "./pages/HomePage";
import LandingPageModern from "./pages/LandingPageModern";
import { REPORT_EXAMPLES_ENABLED } from "./services/reportExamplesService";
import ReportExamplePage from "./pages/ReportExamplePage";
import DashboardModern from "./pages/DashboardModern";
import PartidosModern from "./pages/PartidosPageModern";
import RankingJugadoresModern from "./pages/RankingJugadoresPageModern";
import ActaPartidoModern from "./pages/ActaPartidoPageModern";
import JugadorModern from "./pages/JugadorPageModern";
import EstadisticasJugadoresPageModern from "./pages/EstadisticasJugadoresPageModern";
import ClasificacionModern from "./pages/ClasificacionPageModern";
import ClubesModern from "./pages/ClubesPageModern";
import HerramientasModern from "./pages/HerramientasPageModern";
import RankingEquiposModern from "./pages/RankingEquiposPageModern";
import EquiposModern from "./pages/EquiposPageModern";
import EstadisticasArbitrosModern from "./pages/EstadisticasArbitrosPageModern";
import EstadisticasArbitroDetalleModern from "./pages/EstadisticasArbitroDetallePageModern";
import InformePartidoModern from "./pages/InformePartidoPageModern";
import AnalisisIndividualModern from "./pages/AnalisisIndividualModern";
import AnalisisIndividualDetalleModern from "./pages/AnalisisIndividualDetalleModern";
import ChatAgentPageModern from "./pages/ChatAgentPageModern";
import ScoutingPageModern from "./pages/ScoutingPageModern";
import PlanningPageModern from "./pages/PlanningPageModern";
import AdminAnalyticsPageModern from "./pages/AdminAnalyticsPageModern";
import AdminLicenciasPageModern from "./pages/AdminLicenciasPageModern";
import AdminPromoCodesPageModern from "./pages/AdminPromoCodesPageModern";
import AppVersionsPageModern from "./pages/AppVersionsPageModern";
import FeatureFlagsAdmin from "./components/admin/FeatureFlagsAdmin";
import LicenciasPageModern from "./pages/LicenciasPageModern";
import LicenseManagementPageModern from "./pages/LicenseManagementPageModern";
import DesignSystemDemo from "./components/DesignSystemDemo";
import { Loading } from "./design-system";
import useAppInsights from "./hooks/useAppInsights";
import { AppProvider } from "./context/AppContext";
import { FeatureFlagsProvider } from "./context/FeatureFlagsContext";
import useFeatureFlags from "./hooks/useFeatureFlags";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import LicenseOnboarding from "./components/onboarding/LicenseOnboarding";
import { LicenseUpgradeDialogProvider } from "./components/license/LicenseUpgradeDialog";
import { PageTourProvider } from "./components/onboarding/PageTourProvider";
import RouteTourRegistration from "./components/onboarding/RouteTourRegistration";
import LandingHelpButton from "./components/onboarding/LandingHelpButton";
import { useAuth } from './context/AuthContext';
import { AdminRoute, GuestRoute, ProtectedRoute } from './components/auth/RouteGuards';
import LoginPageModern from './pages/LoginPageModern';
import RegisterPageModern from './pages/RegisterPageModern';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import AdminUsersPage from './pages/AdminUsersPage';
import ManageTeamsPage from './pages/ManageTeamsPage';
import WindowsClientPage from './pages/WindowsClientPage';
import { LicenseFeatureRoute } from './components/license/LicenseGate';
import LicenseStatusBanner from './components/license/LicenseStatusBanner';
import PendingPromoAlert from './components/license/PendingPromoAlert';
import { getStoredThemeMode, saveThemeMode } from './utils/themePreference';
import queryClient from './config/queryClient';
import { DASHBOARD_V2_ENABLED } from './services/dashboardV2Service';
import NavigationMemory from './components/NavigationMemory';
import { getWorkspaceRoute } from './utils/workspaceRoutes';
import './pages/WorkspacePages.css';

const AUTH_PATHS = new Set(['/login', '/register', '/forgot-password', '/reset-password']);

const LiveTagPageModern = lazy(() => import("./pages/LiveTagPageModern"));
const LiveTagSessionPage = lazy(() => import("./pages/LiveTagSessionPage"));
const VideoTagSessionPage = lazy(() => import("./pages/VideoTagSessionPage"));
const DashboardV2 = lazy(() => import("./pages/DashboardV2"));
const TutorialsPageModern = lazy(() => import("./pages/TutorialsPageModern"));
const SharedTacticalBoardPage = lazy(() => import("./pages/SharedTacticalBoardPage"));
const TacticalWorkspacePage = lazy(() => import("./pages/TacticalWorkspacePage"));

const routeFallback = (
  <Box
    sx={{
      minHeight: "60vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <Loading variant="soccer" text="Cargando..." />
  </Box>
);

// FeatureRoute: Guards routes based on feature flags
export const FeatureRoute = ({
  featureName,
  children,
  fallback = "/dashboard",
  renderWhileLoading = true,
}) => {
  const { isFeatureEnabled, loading } = useFeatureFlags();
  // Keep mounted route state while flags are refreshed after the window regains focus.
  if (loading) return renderWhileLoading ? children : routeFallback;

  const enabled = isFeatureEnabled(featureName);

  return enabled ? children : <Navigate to={fallback} replace />;
};

const App = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [themeMode, setThemeMode] = useState(getStoredThemeMode);
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthRoute = AUTH_PATHS.has(location.pathname);
  const { user, isAuthenticated, loading: authLoading, signOut } = useAuth();
  const isWorkspace = DASHBOARD_V2_ENABLED && isAuthenticated && !!getWorkspaceRoute(location.pathname);
  const showPortalShell = location.pathname !== '/' && !location.pathname.startsWith('/ejemplos/informe/') && !location.pathname.startsWith('/pizarra/') && !isAuthRoute && !isWorkspace;
  const theme = useMemo(() => createAppTheme(themeMode), [themeMode]);
  useAppInsights();
  const canAccessAdminAnalytics = isAuthenticated && user?.rol === 'admin';

  useEffect(() => {
    saveThemeMode(themeMode);
  }, [themeMode]);

  const handleLogin = () => {
    navigate('/login');
  };

  const logout = async () => {
    if (!window.dispatchEvent(new Event('tactical-before-signout', { cancelable: true }))) return;
    await signOut();
    navigate('/');
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const toggleThemeMode = () => {
    setThemeMode((currentMode) => (currentMode === 'dark' ? 'light' : 'dark'));
  };
  const workspace = (page, activePath, titleKey) => DASHBOARD_V2_ENABLED ? (
    <Suspense fallback={routeFallback}>
      <DashboardV2 themeMode={themeMode} onToggleTheme={toggleThemeMode} activePath={activePath} titleKey={titleKey}
        notifications={<><LicenseOnboarding /><PendingPromoAlert /><LicenseStatusBanner /></>}>{page}</DashboardV2>
    </Suspense>
  ) : typeof page === 'function' ? page({ favorite: null }) : page;

  if (authLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: 'background.default'
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              width: 40, height: 40,
              border: '3px solid',
              borderColor: 'divider',
              borderTopColor: 'primary.main',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
              '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } }
            }} />
          </Box>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <NavigationMemory>
        <FeatureFlagsProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <LicenseUpgradeDialogProvider>
            <PageTourProvider>
            <RouteTourRegistration pathname={location.pathname} />
            {location.pathname === '/' && <LandingHelpButton />}
            <Box
              sx={{
                backgroundColor: "background.default",
                color: "text.primary",
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                m: 0,
                p: 0,
              }}
            >
            {/* Menú lateral — solo en portal, no en landing */}
            {showPortalShell && (
              <Menu
                open={menuOpen}
                toggleDrawer={toggleMenu}
                isAuthenticated={isAuthenticated}
                handleLogout={logout}
                usuario={user}
              />
            )}

            {/* Header Modernizado — solo en portal, no en landing */}
            {showPortalShell && (
              <ModernHeader
                isAuthenticated={isAuthenticated}
                onLogin={handleLogin}
                onLogout={logout}
                toggleMenu={toggleMenu}
                usuario={user}
                themeMode={themeMode}
                onToggleTheme={toggleThemeMode}
              />
            )}

            {showPortalShell && <LicenseOnboarding />}
            {showPortalShell && <PendingPromoAlert />}
            {showPortalShell && <LicenseStatusBanner />}

          {/* Rutas */}
          <Routes>
            <Route path="/pizarras" element={<ProtectedRoute>{workspace(
              <Suspense fallback={routeFallback}><TacticalWorkspacePage /></Suspense>, '/pizarras'
            )}</ProtectedRoute>} />
            <Route path="/pizarra/:id" element={<Suspense fallback={routeFallback}>
              <SharedTacticalBoardPage themeMode={themeMode} onToggleTheme={toggleThemeMode} />
            </Suspense>} />
            {/* Página de inicio - Nueva landing page comprehensiva */}
            <Route
              path="/"
              element={<LandingPageModern themeMode={themeMode} onToggleTheme={toggleThemeMode} />}
            />
            <Route path="/login" element={<GuestRoute><LoginPageModern onToggleTheme={toggleThemeMode} /></GuestRoute>} />
            <Route path="/register" element={<GuestRoute><RegisterPageModern onToggleTheme={toggleThemeMode} /></GuestRoute>} />
            <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/invitaciones/:token" element={<ProtectedRoute>{workspace(<AcceptInvitationPage />, '/administrar-equipos', 'invitation')}</ProtectedRoute>} />
            <Route path="/perfil" element={<ProtectedRoute>{workspace(<ProfilePage />, '/perfil')}</ProtectedRoute>} />
            <Route path="/tutoriales" element={<ProtectedRoute><Suspense fallback={routeFallback}>
              {workspace(<TutorialsPageModern />, '/tutoriales')}
            </Suspense></ProtectedRoute>} />
            <Route path="/administrar-equipos" element={<ProtectedRoute>{workspace(<ManageTeamsPage />, '/administrar-equipos')}</ProtectedRoute>} />
            {/* Cliente Windows - public page */}
            <Route path="/cliente-windows" element={isAuthenticated ? workspace(<WindowsClientPage />, '/cliente-windows') : <WindowsClientPage />} />
            {REPORT_EXAMPLES_ENABLED && <Route path="/ejemplos/informe/:slug" element={<ReportExamplePage />} />}
            <Route path="/admin/usuarios" element={<AdminRoute>{workspace(<AdminUsersPage />, '/admin/usuarios')}</AdminRoute>} />

            {/* Página de inicio legacy (backup) - solo en desarrollo */}
            {process.env.NODE_ENV === 'development' && (
              <Route path="/home-old" element={<HomePage />} />
            )}

            {/* Dashboard protegido */}
            <Route
              path="/dashboard-v2"
              element={<Navigate to="/dashboard" replace />}
            />
            <Route
              path="/dashboard"
              element={DASHBOARD_V2_ENABLED ? (
                <ProtectedRoute>
                  <Suspense fallback={routeFallback}>
                    <DashboardV2
                      themeMode={themeMode}
                      onToggleTheme={toggleThemeMode}
                      notifications={<><LicenseOnboarding /><PendingPromoAlert /><LicenseStatusBanner /></>}
                    />
                  </Suspense>
                </ProtectedRoute>
              ) : <ProtectedRoute><DashboardModern /></ProtectedRoute>}
            />
            <Route
              path="/dashboard-clasico"
              element={
                isAuthenticated ? <DashboardModern /> : <Navigate to="/" replace />
              }
            />

            {/* Partidos protegido */}
            <Route
              path="/partidos"
              element={
                isAuthenticated ? (DASHBOARD_V2_ENABLED ? <Suspense fallback={routeFallback}>
                  <DashboardV2 themeMode={themeMode} onToggleTheme={toggleThemeMode} activePath="/partidos"
                    notifications={<><LicenseOnboarding /><PendingPromoAlert /><LicenseStatusBanner /></>}>
                    {({ favorite }) => <PartidosModern favorite={favorite} />}
                  </DashboardV2>
                </Suspense> : <PartidosModern />) : <Navigate to="/" replace />
              }
            />

            {/* Partidos protegido */}
            <Route
              path="/rankingJugadores"
              element={
                isAuthenticated ? (
                  workspace(<RankingJugadoresModern />, '/rankingJugadores')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/jugadoresSimilares"
              element={
                isAuthenticated ? (
                  workspace(<RankingJugadoresModern similarityPage />, '/rankingJugadores')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/compararJugadores"
              element={
                isAuthenticated ? (
                  workspace(<RankingJugadoresModern comparisonPage />, '/rankingJugadores')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/actaPartido"
              element={
                isAuthenticated ? (
                  <LicenseFeatureRoute permission="reports.view">
                    {workspace(<ActaPartidoModern />, '/partidos', 'matches:record.title')}
                  </LicenseFeatureRoute>
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/estadisticas"
              element={
                isAuthenticated ? (
                  workspace(<LicenseFeatureRoute permission="analytics.view">
                    <EstadisticasJugadoresPageModern />
                  </LicenseFeatureRoute>, '/rankingJugadores')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/jugador"
              element={
                isAuthenticated ? (
                  workspace(<LicenseFeatureRoute permission="analytics.view">
                    <JugadorModern />
                  </LicenseFeatureRoute>, '/rankingJugadores')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/clasificacion"
              element={
                isAuthenticated ? (
                  DASHBOARD_V2_ENABLED ? <Suspense fallback={routeFallback}>
                    <DashboardV2 themeMode={themeMode} onToggleTheme={toggleThemeMode} activePath="/clasificacion">
                      {({ favorite }) => <ClasificacionModern favorite={favorite} />}
                    </DashboardV2>
                  </Suspense> : <ClasificacionModern />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/rankingEquipos"
              element={
                isAuthenticated ? (
                  workspace(<RankingEquiposModern />, '/rankingEquipos')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/estadisticasEquipos"
              element={
                isAuthenticated ? (
                  workspace(({ favorite }) => <LicenseFeatureRoute permission="analytics.view">
                    <EquiposModern favorite={favorite} />
                  </LicenseFeatureRoute>, '/estadisticasEquipos')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/estadisticasArbitros"
              element={
                isAuthenticated ? (
                  workspace(<LicenseFeatureRoute permission="analytics.view">
                    <EstadisticasArbitrosModern />
                  </LicenseFeatureRoute>, '/estadisticasArbitros')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/estadisticasArbitros/:idArbitro"
              element={
                isAuthenticated ? (
                  workspace(<LicenseFeatureRoute permission="analytics.view">
                    <EstadisticasArbitroDetalleModern />
                  </LicenseFeatureRoute>, '/estadisticasArbitros')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/clubes"
              element={
                isAuthenticated ? workspace(<ClubesModern />, '/clubes') : <Navigate to="/" replace />
              }
            />

            <Route
              path="/herramientas"
              element={
                isAuthenticated ? workspace(<HerramientasModern />, '/herramientas') : <Navigate to="/" replace />
              }
            />

            <Route
              path="/informePartido"
              element={
                isAuthenticated ? (
                  workspace(<InformePartidoModern />, '/partidos')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/analisisIndividual"
              element={
                isAuthenticated ? (
                  workspace(<LicenseFeatureRoute permission="scouting.view">
                    <AnalisisIndividualModern />
                  </LicenseFeatureRoute>, '/analisisIndividual')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/analisisIndividual/microconcepto/:id"
              element={
                isAuthenticated ? (
                  workspace(<LicenseFeatureRoute permission="scouting.view">
                    <AnalisisIndividualDetalleModern />
                  </LicenseFeatureRoute>, '/analisisIndividual')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/agente"
              element={
                isAuthenticated ? (
                  workspace(<FeatureRoute featureName="chatIA">
                    <LicenseFeatureRoute permission="ai_agent.use">
                      <ChatAgentPageModern />
                    </LicenseFeatureRoute>
                  </FeatureRoute>, '/agente')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* Scouting protegido */}
            <Route
              path="/scouting"
              element={
                isAuthenticated ? (
                  workspace(<LicenseFeatureRoute permission="scouting.view">
                    <ScoutingPageModern />
                  </LicenseFeatureRoute>, '/scouting')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/planificacion"
              element={
                <ProtectedRoute>
                  {workspace(<FeatureRoute featureName="planificacion" renderWhileLoading={false}>
                    <LicenseFeatureRoute permission="planning.manage">
                      <PlanningPageModern usuario={user} />
                    </LicenseFeatureRoute>
                  </FeatureRoute>, '/planificacion')}
                </ProtectedRoute>
              }
            />

            <Route
              path="/live-tag"
              element={
                isAuthenticated ? (
                  workspace(<Suspense fallback={routeFallback}>
                    <LicenseFeatureRoute permission="live_tag.use">
                      <LiveTagPageModern />
                    </LicenseFeatureRoute>
                  </Suspense>, '/live-tag')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/live-tag/session/:matchId"
              element={
                isAuthenticated ? (
                  workspace(<Suspense fallback={routeFallback}>
                    <LicenseFeatureRoute permission="live_tag.use">
                      <LiveTagSessionPage />
                    </LicenseFeatureRoute>
                  </Suspense>, '/live-tag')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/live-tag/session/:matchId/video"
              element={
                isAuthenticated ? (
                  workspace(<Suspense fallback={routeFallback}>
                    <LicenseFeatureRoute permission="live_tag.use">
                      <VideoTagSessionPage />
                    </LicenseFeatureRoute>
                  </Suspense>, '/live-tag')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* Licencias */}
            <Route
              path="/licencias"
              element={
                isAuthenticated ? (
                  workspace(<FeatureRoute featureName="licencias">
                    <LicenciasPageModern />
                  </FeatureRoute>, '/licencias')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route
              path="/mis-licencias"
              element={
                isAuthenticated ? (
                  workspace(<FeatureRoute featureName="licencias">
                    <LicenseManagementPageModern />
                  </FeatureRoute>, '/mis-licencias')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* Admin Analytics - solo rol admin */}
            <Route
              path="/admin/analytics"
              element={
                canAccessAdminAnalytics ? (
                  workspace(<AdminAnalyticsPageModern />, '/admin/analytics')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* Admin Licencias - solo rol admin */}
            <Route
              path="/admin/licencias"
              element={
                canAccessAdminAnalytics ? (
                  workspace(<AdminLicenciasPageModern />, '/admin/licencias')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            {/* Admin Promo Codes - solo rol admin */}
            <Route
              path="/admin/promo-codes"
              element={<AdminRoute>{workspace(<AdminPromoCodesPageModern />, '/admin/promo-codes')}</AdminRoute>}
            />

            <Route
              path="/admin/versiones"
              element={
                user?.rol === "admin" ? (
                  workspace(<AppVersionsPageModern />, '/admin/versiones')
                ) : (
                  <Navigate to="/unauthorized" replace />
                )
              }
            />

            {/* Admin Feature Flags - solo rol admin */}
            <Route
              path="/admin/feature-flags"
              element={
                canAccessAdminAnalytics ? (
                  workspace(<FeatureFlagsAdmin />, '/admin/feature-flags')
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />

            <Route
              path="/unauthorized"
              element={
                <Box sx={{ p: 4 }}>
                  <Alert severity="error">
                    Acceso denegado. Solo los administradores pueden acceder a esta sección.
                  </Alert>
                </Box>
              }
            />

            {/* Ruta temporal para demo del Design System - solo en desarrollo */}
            {process.env.NODE_ENV === 'development' && (
              <Route path="/design-demo" element={<DesignSystemDemo />} />
            )}

            {/* Redirección predeterminada */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Footer */}
          {!isAuthRoute && !isWorkspace && <Footer />}
        </Box>
          </PageTourProvider>
          </LicenseUpgradeDialogProvider>
        </ThemeProvider>
      </FeatureFlagsProvider>
      </NavigationMemory>
      </AppProvider>

      {/* React Query Devtools - solo en desarrollo */}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" position="bottom" />
      )}
    </QueryClientProvider>
  );
};

export default App;
